import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  ArrowLeft,
  Download,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  User,
  Mail,
  Phone,
  Calendar as CalendarIcon,
  FileText,
  Edit2,
  Save,
  X,
  ListChecks,
  Circle,
  Loader2,
  Euro,
  TrendingUp,
  MapPin,
  Ruler,
  Shield,
  Flag,
  Upload,
  File,
  Trash2,
  Building,
  Home,
  Pencil,
  MessageSquare,
  Plus,
  FileSpreadsheet,
  ChevronDown,
  Send,
  CheckCheck,
  ToggleLeft,
  UserCheck,
  Hammer,
  Calculator,
  Layers,
  StickyNote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SimulationResultsTab } from "./dossier/SimulationResultsTab";
import TravauxTab from "./dossier/TravauxTab";
import EtapesPacSsc from "@/components/EtapesPacSsc";
import Etapes174 from "@/components/Etapes174";
import SendUploadLinkModal from "@/components/SendUploadLinkModal";
import { isRestrictedUser } from "./Login";
import { SCIEtapesTab, SCIInfoTab, SCITravauxTab } from "@/components/SCIMultiLogement";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const PROFILE_LABELS = {
  bailleur: "Bailleur",
  proprietaire_occupant: "Propriétaire Occupant",
  sci: "SCI"
};

const STATUS_CONFIG = {
  complete: { label: "Complet", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  in_progress: { label: "En cours", icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  incomplete: { label: "Incomplet", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-500/10", border: "border-red-500/30" }
};

const DOC_STATUS_CONFIG = {
  validated: { label: "Valide", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  awaiting_validation: { label: "En attente de validation", icon: Clock, color: "text-blue-600", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  pending: { label: "En attente", icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  rejected: { label: "Rejete", icon: XCircle, color: "text-red-600", bg: "bg-red-500/10", border: "border-red-500/30" }
};

const COMP_DOC_STATUS_CONFIG = {
  non_demande: { label: "Non demandé", icon: Circle, color: "text-muted-foreground", bg: "bg-muted", border: "border-border" },
  en_attente: { label: "En attente", icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  transmis: { label: "Transmis", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" }
};

const PAYMENT_STEPS = ["prime_cee", "prime_apa", "prime_mpr"];

const formatCurrency = (amount) => {
  if (!amount) return "0 €";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const PRECARITE_CONFIG = {
  BLEU: { label: "Bleu - Modeste", color: "bg-blue-500" },
  JAUNE: { label: "Jaune - Intermédiaire", color: "bg-yellow-500/100" },
  VIOLET: { label: "Violet - Supérieur", color: "bg-purple-500/100" }
};

const ZONE_LABELS = {
  H1: "H1 - Nord et Est",
  H2: "H2 - Ouest et Centre",
  H3: "H3 - Sud et Méditerranée"
};

export default function DossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingDoc, setSavingDoc] = useState(null);
  const [savingStep, setSavingStep] = useState(null);
  const [amountInputs, setAmountInputs] = useState({});
  const [completionDate, setCompletionDate] = useState(null);
  const [compDocName, setCompDocName] = useState("");
  const [compDocStatus, setCompDocStatus] = useState("non_demande");
  const [savingCompDoc, setSavingCompDoc] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingApf, setUploadingApf] = useState(false);
  const isRestricted = isRestrictedUser();
  
  // Edit devis/marge states
  const [editingDevis, setEditingDevis] = useState(false);
  const [devisAmount, setDevisAmount] = useState(0);
  const [editingMarge, setEditingMarge] = useState(false);
  const [margeAmount, setMargeAmount] = useState(0);
  
  // Notes state
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  
  // Upload link modal state
  const [showUploadLinkModal, setShowUploadLinkModal] = useState(false);
  
  // Step 2 (devis signé) reminder for Travaux tab
  const [showTravauxReminder, setShowTravauxReminder] = useState(false);

  // P2-7: Contrôles financiers MPR
  const [periodeMpr, setPeriodeMpr] = useState("apres_2026");
  const [montantDevisMpr, setMontantDevisMpr] = useState(0);
  const [montantMarchandise, setMontantMarchandise] = useState(0);
  const [coutType, setCoutType] = useState("regie"); // "regie" ou "lead"
  const [savingFinancial, setSavingFinancial] = useState(false);
  
  // P2-8: Attribution
  const [assignedTo, setAssignedTo] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [savingAttribution, setSavingAttribution] = useState(false);
  const isAdmin = !isRestricted; // Admin = not restricted (admin_full only)
  const canEditAttribution = true; // All admin roles can edit attribution

  const is174 = dossier?.programme === "174";
  const isCumule = dossier?.programme === "cumule";
  const is174OrCumule = is174 || isCumule;
  const isSCIMulti = dossier?.is_sci_multi === true;
  
  // Check if last step (step 9) is done
  const isLastStepDone = () => {
    if (!dossier?.steps || dossier.steps.length === 0) return false;
    // Exclude SAV steps — find last non-SAV step
    const nonSav = dossier.steps.filter(s => !s.key?.toLowerCase().includes("sav"));
    if (nonSav.length === 0) return false;
    const lastNonSav = nonSav[nonSav.length - 1];
    return lastNonSav?.status === 'done';
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const currentUser = localStorage.getItem("label_energie_user") || "";
      await axios.post(`${API}/dossiers/${id}/files?uploaded_by=${encodeURIComponent(currentUser)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Fichier uploadé avec succès");
      fetchDossier();
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Erreur lors de l'upload du fichier");
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };

  const handleApfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }

    setUploadingApf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/dossiers/${id}/apf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Afficher un résumé des données extraites
      const extracted = response.data.extracted_data;
      if (extracted?.numero_aaf) {
        toast.success(
          `APF importé ! Référence: ${extracted.numero_aaf}${extracted.numeros_dossiers?.length > 0 ? ` - ${extracted.numeros_dossiers.length} dossier(s) trouvé(s)` : ''}`,
          { duration: 5000 }
        );
      } else {
        toast.success("APF importé avec succès");
      }
      
      fetchDossier();
    } catch (error) {
      console.error("Error uploading APF:", error);
      toast.error("Erreur lors de l'import de l'APF");
    } finally {
      setUploadingApf(false);
      event.target.value = '';
    }
  };

  const handleFileDelete = async (fileId) => {
    try {
      await axios.delete(`${API}/dossiers/${id}/files/${fileId}`);
      toast.success("Fichier supprimé");
      fetchDossier();
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleFileDownload = (fileId, fileName) => {
    window.open(`${API}/dossiers/${id}/files/${fileId}`, '_blank');
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchDossier();
    // Fetch users list for attribution
    axios.get(`${API}/users`).then(r => setUsersList(r.data)).catch(() => {});
  }, [id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Pure function: compute marge from dossier data (same logic as calculateDynamicMarge)
  const computeMargeFromDossier = (d) => {
    if (!d) return null;
    const prog = (d.programme || "").toUpperCase();
    const is174Prog = prog === "174";
    const isMprProg = ["MPR", "PAC/SSC", "PAC", "PAC + CESI"].includes(prog);
    const isCumuleProg = prog === "CUMULE";
    const devisStep = d.steps?.find(s => s.key === "devis_signe");
    const mprDevisStep = d.steps?.find(s => s.key === "mpr_devis_signe");
    const hasDevis = (devisStep?.status === "done" && devisStep?.amount > 0) || (mprDevisStep?.status === "done" && mprDevisStep?.amount > 0);
    if (!hasDevis) return null;
    
    const devisAmt = devisStep?.amount || 0;
    const mprDevisAmt = mprDevisStep?.amount || 0;
    const pac = d.montant_pac || 0;
    const ssc = d.montant_ssc || 0;
    const precarite = (d.precarite || "BLEU").toUpperCase();
    const isJaune = precarite === "JAUNE";
    const surface = d.metrage || 0;
    const coutPrec = isJaune ? 1440 : 1800;
    const assignedTo = d.assigned_to || "";
    const isAssignedToAdmin = assignedTo === "Renova" || assignedTo === "Admin";
    let coutRegie = 0;
    if (!isAssignedToAdmin) {
      coutRegie = surface < 70 ? 2000 : surface <= 130 ? 2500 : 3000;
    }
    const periodo = d.periode_mpr || "apres_2026";
    
    if (isMprProg) {
      if (periodo !== "apres_2026") {
        const base = isJaune ? 3400 : 6800;
        return pac + ssc > 0 ? base - ((pac + ssc) - 6580) : base;
      }
      const amt = mprDevisAmt || devisAmt;
      return amt - pac - ssc - coutRegie - 4200 - 80 - coutPrec;
    }
    if (is174Prog) {
      let travaux = 0;
      const td = d.travaux_devis;
      if (td) {
        const items = td.items || td.logements || [];
        if (Array.isArray(items)) items.forEach(it => { if (it) travaux += it.total || (it.qty || 0) * (it.unit_price || 0); });
      }
      if (travaux === 0 && d.montant_devis_174) travaux = d.montant_devis_174;
      return devisAmt - travaux - coutRegie;
    }
    if (isCumuleProg) {
      // MPR part
      let mprMarge = 0;
      if (mprDevisAmt > 0) mprMarge = mprDevisAmt - pac - ssc - 4200 - 80 - coutPrec;
      // 174 part
      let m174Marge = 0;
      if (devisAmt > 0) {
        let travaux = 0;
        const td = d.travaux_devis;
        if (td) {
          const items = td.items || td.logements || [];
          if (Array.isArray(items)) items.forEach(it => { if (it) travaux += it.total || (it.qty || 0) * (it.unit_price || 0); });
        }
        if (travaux === 0 && d.montant_devis_174) travaux = d.montant_devis_174;
        m174Marge = devisAmt - travaux;
      }
      return mprMarge + m174Marge - coutRegie;
    }
    return null;
  };

  const fetchDossier = async () => {
    try {
      const response = await axios.get(`${API}/dossiers/${id}`);
      setDossier(response.data);
      setEditData({
        client_name: response.data.client_name,
        client_email: response.data.client_email || "",
        client_phone: response.data.client_phone || "",
        programme: response.data.programme || "",
        zone: response.data.zone || "",
        metrage: response.data.metrage || "",
        precarite: response.data.precarite || "",
        notes: response.data.notes || "",
        code_postal: response.data.code_postal || "",
        adresse_logement: response.data.adresse_logement || "",
        // Champs 174
        organisme_bailleur: response.data.organisme_bailleur || "",
        nom_representant: response.data.nom_representant || "",
        tel_representant: response.data.tel_representant || "",
        fixe_representant: response.data.fixe_representant || "",
        email_representant: response.data.email_representant || "",
        adresse_bailleur: response.data.adresse_bailleur || "",
        nom_residence: response.data.nom_residence || "",
        adresse_residence: response.data.adresse_residence || "",
        coordonnees_gardien: response.data.coordonnees_gardien || "",
        nombre_logements: response.data.nombre_logements || 1,
        type_logements: response.data.type_logements || "appartement"
      });
      
      // Initialize amount inputs from existing data
      const amounts = {};
      response.data.steps?.forEach(step => {
        if (PAYMENT_STEPS.includes(step.key) && step.amount) {
          amounts[step.key] = step.amount.toString();
        }
      });
      setAmountInputs(amounts);
      
      // Initialize devis amount for 174 or cumule
      if (response.data.programme === "174") {
        setDevisAmount(response.data.montant_devis_174 || 0);
      } else if (response.data.programme === "cumule") {
        setDevisAmount(response.data.total_aides || 0);
      }
      
      // Initialize marge
      setMargeAmount(response.data.marge_realisee || response.data.marge_prevue || 0);
      
      // Initialize completion date
      const installStep = response.data.steps?.find(s => s.key === 'dossier_installe');
      if (installStep?.completion_date) {
        setCompletionDate(new Date(installStep.completion_date));
      }
      
      // Initialize complementary doc
      setCompDocName(response.data.complementary_doc_name || "");
      setCompDocStatus(response.data.complementary_doc_status || "non_demande");
      
      // Initialize P2-7 fields
      setPeriodeMpr(response.data.periode_mpr || "apres_2026");
      setMontantDevisMpr(response.data.montant_devis_mpr || 0);
      setMontantMarchandise(response.data.montant_marchandise || 0);
      setCoutType(response.data.cout_type || "regie");
      
      // Initialize P2-8 field
      setAssignedTo(response.data.assigned_to || "");
      
      // Auto-sync: save dynamically calculated marge to DB
      const d = response.data;
      if (d.id && d.id.includes("-") && !d._from_etude) {
        const calcM = computeMargeFromDossier(d);
        if (calcM !== null) {
          const cur = d.marge_prevue || 0;
          if (Math.abs(cur - calcM) > 1) {
            axios.put(`${API}/dossiers/${d.id}`, { marge_prevue: calcM }).catch(() => {});
          }
        }
      }
    } catch (error) {
      // Fallback: if dossier not found, try to load from etudes collection and create a dossier view
      try {
        const etudeRes = await axios.get(`${API}/etudes/${id}`);
        const e = etudeRes.data;
        // Build a dossier-like object from etude data
        const dossierFromEtude = {
          id: e.id,
          client_name: e.beneficiaire?.nom || "",
          client_phone: e.beneficiaire?.telephone || "",
          client_email: e.beneficiaire?.email || "",
          adresse_logement: e.beneficiaire?.adresse || "",
          profile: e.profil || "proprietaire_occupant",
          programme: e.recommendation || "MPR",
          zone: e.zone || "H1",
          metrage: e.surface || 0,
          precarite: e.precarite || "BLEU",
          notes: e.notes_text || "",
          assigned_to: e.source || "",
          created_by: e.created_by || e.source || "",
          created_at: e.created_at,
          steps: e.steps || [],
          steps_progress: 0,
          documents: e.documents || [],
          status: "incomplete",
          statut_dossier: e.status || "rdv_admin",
          montant_pac: e.montant_pac || 0,
          montant_ssc: e.montant_ssc || 0,
          montant_marchandise: e.montant_marchandise || 0,
          montant_devis_174: e.montant_devis_174 || 0,
          total_payments: e.total_payments || 0,
          quote_amount: e.quote_amount || 0,
          marge_prevue: e.marge_prevue || 0,
          marge_realisee: e.marge_realisee || 0,
          total_aides: e.total_aides || 0,
          mpr_calcule: e.mpr_calcule || 0,
          cee_calcule: e.cee_calcule || 0,
          travaux_devis: e.travaux_devis || null,
          _from_etude: true,
        };
        setDossier(dossierFromEtude);
        setEditData({
          client_name: dossierFromEtude.client_name,
          client_email: dossierFromEtude.client_email,
          client_phone: dossierFromEtude.client_phone,
          programme: dossierFromEtude.programme,
          zone: dossierFromEtude.zone,
          metrage: dossierFromEtude.metrage,
          precarite: dossierFromEtude.precarite,
          notes: dossierFromEtude.notes,
          code_postal: "",
          adresse_logement: dossierFromEtude.adresse_logement,
          organisme_bailleur: "", nom_representant: "", tel_representant: "",
          fixe_representant: "", email_representant: "", adresse_bailleur: "",
          nom_residence: "", adresse_residence: "", coordonnees_gardien: "",
          nombre_logements: 1, type_logements: "appartement"
        });
        setAssignedTo(dossierFromEtude.assigned_to || "");
      } catch (err) {
        console.error("Error fetching dossier:", error);
        toast.error("Erreur lors du chargement du dossier");
        navigate("/dossiers");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {

    // ─── Calcul de marge ───
  const calculateDevisMarge = (d = dossier) => {
    if (!d) return null;
    const nbLogements = d.nombre_logements || 1;
    
    // Déterminer le coût régie/lead
    const assignedUser = usersList.find(u => u.username === d.assigned_to);
    const isRegieUser = assignedUser?.role === "regie";
    let coutRegieOuLead = 2500; // Par défaut pour telepro/admin
    if (isRegieUser && assignedUser?.commissions) {
      // Commission de la régie (regieOptions du simulateur)
      const c = assignedUser.commissions;
      coutRegieOuLead = parseFloat(c.commission_lead) || parseFloat(c.cout_par_dossier) || 2500;
    }
    
    const programme = d.programme?.toLowerCase();
    
    if (programme === "mpr") {
      const montantDevis = d.montant_devis_174 || 0;
      const pac = d.montant_pac || 0;
      const ssc = d.montant_ssc || 0;
      const precarite = (d.precarite || "").toLowerCase();
      const penalitePrecarite = precarite.includes("bleu") ? 1800 : 1440; // bleu = 1800, jaune = 1440
      // MPR: Devis - 4200 - PAC - SSC - (cout_regie_ou_lead * nbre_logements) - 80 - (1800 si bleu / 1440 si jaune)
      return montantDevis - 4200 - pac - ssc - (coutRegieOuLead * nbLogements) - 80 - penalitePrecarite;
    }
    
    if (programme === "174") {
      const montantDevis = d.montant_devis_174 || 0;
      const coutTravaux = d.travaux_devis?.totalCost || 0;
      // 174: Devis - coût travaux calculé - (cout_regie_ou_lead * nbre_logements)
      return montantDevis - coutTravaux - (coutRegieOuLead * nbLogements);
    }
    
    if (programme === "cumule") {
      // Cumulée = MPR part + 174 part
      const devisMpr = (d.total_aides || 0) - (d.montant_devis_174 || 0);
      const pac = d.montant_pac || 0;
      const ssc = d.montant_ssc || 0;
      const precarite = (d.precarite || "").toLowerCase();
      const penalitePrecarite = precarite.includes("bleu") ? 1800 : 1440;
      const margeMpr = devisMpr - 4200 - pac - ssc - (coutRegieOuLead * nbLogements) - 80 - penalitePrecarite;
      
      const montantDevis174 = d.montant_devis_174 || 0;
      const coutTravaux = d.travaux_devis?.totalCost || 0;
      const marge174 = montantDevis174 - coutTravaux - (coutRegieOuLead * nbLogements);
      
      return margeMpr + marge174;
    }
    
    return null;
  };

    try {
      const payload = {
        ...editData,
        metrage: editData.metrage ? parseFloat(editData.metrage) : null,
        programme: editData.programme || null,
        zone: editData.zone || null,
        precarite: editData.precarite || null
      };
      await axios.put(`${API}/dossiers/${id}`, payload);
      toast.success("Dossier mis à jour");
      setEditMode(false);
      fetchDossier();
    } catch (error) {
      console.error("Error updating dossier:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDocumentStatusChange = async (docKey, newStatus) => {
    setSavingDoc(docKey);
    try {
      await axios.put(`${API}/dossiers/${id}/documents/${docKey}`, {
        status: newStatus,
        comment: null
      });
      toast.success("Statut du document mis à jour");
      fetchDossier();
    } catch (error) {
      console.error("Error updating document:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingDoc(null);
    }
  };

  // Valider tous les documents d'un coup
  const [validatingAllDocs, setValidatingAllDocs] = useState(false);
  
  const handleValidateAllDocuments = async () => {
    const pendingDocs = dossier.documents.filter(doc => doc.status !== 'validated');
    if (pendingDocs.length === 0) {
      toast.info("Tous les documents sont déjà validés");
      return;
    }
    
    setValidatingAllDocs(true);
    try {
      // Valider chaque document non-validé
      for (const doc of pendingDocs) {
        await axios.put(`${API}/dossiers/${id}/documents/${doc.key}`, {
          status: 'validated',
          comment: null
        });
      }
      toast.success(`${pendingDocs.length} document(s) validé(s)`);
      fetchDossier();
    } catch (error) {
      console.error("Error validating all documents:", error);
      toast.error("Erreur lors de la validation");
    } finally {
      setValidatingAllDocs(false);
    }
  };

  // Check if a step can be modified
  // Steps 1-6 are sequential (0-5 in index)
  // Steps 7, 8, 9 (indices 6, 7, 8) can be validated in any order once step 6 (index 5) is done
  // Step 10 (SAV, index 9) requires all payment steps (7, 8, 9) to be done
  const canModifyStep = (stepIndex) => {
    if (stepIndex === 0) return true;
    
    // Only apply special logic for MPR program (not 174 or cumule)
    if (!is174 && !isCumule) {
      // Steps 7, 8, 9 (prime_cee, prime_apa, prime_mpr) - indices 6, 7, 8
      // Can be validated in any order once step 6 (dossier_installe, index 5) is done
      if (stepIndex >= 6 && stepIndex <= 8) {
        const step6 = dossier.steps[5]; // dossier_installe
        return step6?.status === 'done';
      }
      
      // Step 10 (SAV, index 9) requires all payment steps to be done
      if (stepIndex === 9) {
        const paymentSteps = [dossier.steps[6], dossier.steps[7], dossier.steps[8]];
        return paymentSteps.every(s => s?.status === 'done');
      }
    }
    
    // Default: previous step must be done (for steps 1-6 and all 174 steps)
    const prevStep = dossier.steps[stepIndex - 1];
    return prevStep?.status === 'done';
  };

  const handleStepStatusChange = async (stepKey, newStatus, subStatus = null, amount = null, compDate = null) => {
    const stepIndex = dossier.steps.findIndex(s => s.key === stepKey);
    
    // Check if previous step is done
    if (!canModifyStep(stepIndex)) {
      toast.error("Vous devez d'abord terminer l'étape précédente");
      return;
    }
    
    setSavingStep(stepKey);
    try {
      const step = dossier.steps.find(s => s.key === stepKey);
      const payload = {
        status: newStatus,
        sub_status: subStatus !== null ? subStatus : step?.sub_status,
        comment: null,
        amount: amount !== null ? amount : step?.amount
      };
      
      // Add completion date for dossier_installe
      if (stepKey === 'dossier_installe' && compDate !== null) {
        payload.completion_date = compDate;
      }
      
      await axios.put(`${API}/dossiers/${id}/steps/${stepKey}`, payload);
      toast.success("Étape mise à jour");
      
      // When devis_signe is validated to "done", calculate and save marge
      if (stepKey === 'devis_signe' && newStatus === 'done') {
        // Refresh dossier first to get latest data
        const freshRes = await axios.get(`${API}/dossiers/${id}`);
        const freshDossier = freshRes.data;
        
        const marge = calculateDevisMarge(freshDossier);
        if (marge !== null) {
          const updatePayload = { marge_prevue: marge };
          // Also update montant_devis_174 from the devis step amount if available
          const devisStep = freshDossier.steps?.find(s => s.key === 'devis_signe');
          if (devisStep?.amount && is174OrCumule) {
            updatePayload.montant_devis_174 = devisStep.amount;
          }
          await axios.put(`${API}/dossiers/${id}`, updatePayload);
        }
      }
      
      // Also handle mpr_devis_signe and 174_devis_signe for cumule
      if ((stepKey === 'mpr_devis_signe' || stepKey === '174_devis_signe') && newStatus === 'done') {
        const freshRes = await axios.get(`${API}/dossiers/${id}`);
        const marge = calculateDevisMarge(freshRes.data);
        if (marge !== null) {
          await axios.put(`${API}/dossiers/${id}`, { marge_prevue: marge });
        }
      }
      
      // Update montant_encaisse from last step (paye) amount
      if (stepKey === 'paye' && amount && is174OrCumule) {
        await axios.put(`${API}/dossiers/${id}`, { montant_encaisse: amount, montant_devis_174: amount });
      }
      
      fetchDossier();
    } catch (error) {
      console.error("Error updating step:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingStep(null);
    }
  };

  // Handler pour les étapes 174 - mise à jour de date
  const handleStepDateChange = async (stepKey, dateStr) => {
    setSavingStep(stepKey);
    try {
      const step = dossier.steps.find(s => s.key === stepKey);
      const payload = {
        status: step?.status || 'not_started',
        sub_status: step?.sub_status,
        comment: null,
        amount: step?.amount,
        completion_date: dateStr
      };
      
      await axios.put(`${API}/dossiers/${id}/steps/${stepKey}`, payload);
      toast.success("Date mise à jour");
      fetchDossier();
    } catch (error) {
      console.error("Error updating step date:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingStep(null);
    }
  };

  // Handler pour les étapes 174 - mise à jour de montant
  const handleStepAmountChange = async (stepKey, amount) => {
    setSavingStep(stepKey);
    try {
      const step = dossier.steps.find(s => s.key === stepKey);
      const payload = {
        status: step?.status || 'not_started',
        sub_status: step?.sub_status,
        comment: null,
        amount: amount,
        completion_date: step?.completion_date
      };
      
      await axios.put(`${API}/dossiers/${id}/steps/${stepKey}`, payload);
      toast.success("Montant mis à jour");
      fetchDossier();
    } catch (error) {
      console.error("Error updating step amount:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingStep(null);
    }
  };

  const handleAmountChange = (stepKey, value) => {
    setAmountInputs(prev => ({ ...prev, [stepKey]: value }));
  };

  const handleAmountSave = async (stepKey) => {
    const stepIndex = dossier.steps.findIndex(s => s.key === stepKey);
    if (!canModifyStep(stepIndex)) {
      toast.error("Vous devez d'abord terminer l'étape précédente");
      return;
    }
    
    const amount = parseFloat(amountInputs[stepKey]) || 0;
    const step = dossier.steps.find(s => s.key === stepKey);
    await handleStepStatusChange(stepKey, step?.status || 'not_started', step?.sub_status, amount, null);
  };

  const handleCompletionDateSave = async (date) => {
    setCompletionDate(date);
    const step = dossier.steps.find(s => s.key === 'dossier_installe');
    const dateStr = date ? format(date, 'yyyy-MM-dd') : null;
    await handleStepStatusChange('dossier_installe', step?.status || 'not_started', step?.sub_status, step?.amount, dateStr);
  };

  const handleSousTraitantChange = async (value) => {
    try {
      await axios.put(`${API}/dossiers/${id}`, { sous_traitant: value });
      toast.success("Poseur enregistré");
      fetchDossier();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleCompDocUpdate = async (newStatus) => {
    setSavingCompDoc(true);
    try {
      await axios.put(`${API}/dossiers/${id}/complementary-doc`, {
        name: compDocName,
        status: newStatus
      });
      setCompDocStatus(newStatus);
      toast.success("Document complémentaire mis à jour");
      fetchDossier();
    } catch (error) {
      console.error("Error updating complementary doc:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingCompDoc(false);
    }
  };

  const handleCompDocNameSave = async () => {
    setSavingCompDoc(true);
    try {
      await axios.put(`${API}/dossiers/${id}/complementary-doc`, {
        name: compDocName,
        status: compDocStatus
      });
      toast.success("Nom du document mis à jour");
    } catch (error) {
      console.error("Error updating complementary doc name:", error);
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingCompDoc(false);
    }
  };

  // Save devis amount for 174
  const handleSaveDevis = async () => {
    try {
      await axios.put(`${API}/dossiers/${id}`, { montant_devis_174: devisAmount });
      toast.success("Montant du devis mis à jour");
      setEditingDevis(false);
      fetchDossier();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Save marge (dans marge_prevue si pas terminé, marge_realisee si terminé)
  const handleSaveMarge = async () => {
    try {
      const field = isLastStepDone() ? 'marge_realisee' : 'marge_prevue';
      await axios.put(`${API}/dossiers/${id}`, { [field]: margeAmount });
      toast.success("Marge mise à jour");
      setEditingMarge(false);
      fetchDossier();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Add note
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const currentUser = localStorage.getItem("label_energie_user") || "Inconnu";
      await fetch(`${API}/dossiers/${id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: newNote.trim(), author: currentUser }),
      });
      toast.success("Note ajoutée");
      setNewNote("");
      fetchDossier();
    } catch (err) {
      toast.error("Erreur lors de l'ajout de la note");
    } finally {
      setSavingNote(false);
    }
  };

  // P2-7: Save financial controls + auto-persist marge
  const handleSaveFinancial = async (field, value) => {
    setSavingFinancial(true);
    try {
      const updateData = { [field]: value };
      // If saving PAC/SSC, also recalculate and persist marge
      if (field === "montant_pac" || field === "montant_ssc") {
        const pac = field === "montant_pac" ? value : (dossier.montant_pac || 0);
        const ssc = field === "montant_ssc" ? value : (dossier.montant_ssc || 0);
        if (pac + ssc > 0) {
          const prec = (dossier.precarite || "").toUpperCase();
          const base = prec === "JAUNE" ? 3400 : 6800;
          const marge = base - ((pac + ssc) - 6580);
          updateData.marge_prevue = marge;
        }
      }
      await axios.put(`${API}/dossiers/${id}`, updateData);
      toast.success("Mis à jour");
      fetchDossier();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setSavingFinancial(false);
    }
  };

  // P2-7: Calculate dynamic margin for MPR (après 2026)
  const calculateDynamicMarge = () => {
    if (!dossier || is174) return 0;
    
    // Sum of 3 encaissés
    const encaisseCee = paymentBreakdownRef?.cee || 0;
    const encaisseApa = paymentBreakdownRef?.apa || 0;
    const encaisseMpr = paymentBreakdownRef?.mpr || 0;
    const totalEncaisse = encaisseCee + encaisseApa + encaisseMpr;
    
    // Use encaissé sum, or montant devis if nothing encaissé
    const revenus = totalEncaisse > 0 ? totalEncaisse : montantDevisMpr;
    
    // Cout régie or lead
    let coutRegieLead = 0;
    const programme = dossier.programme || "MPR";
    const surface = dossier.metrage || 0;
    const precarite = dossier.precarite || "";
    
    if (coutType === "regie") {
      // Régie: seulement pour BLEU
      if (precarite === "BLEU") {
        if (surface < 70) {
          if (programme === "cumule") coutRegieLead = 3000;
          else if (programme === "174") coutRegieLead = 1000;
          else coutRegieLead = 2000; // MPR
        } else if (surface <= 130) {
          if (programme === "cumule") coutRegieLead = 4000;
          else if (programme === "174") coutRegieLead = 1500;
          else coutRegieLead = 2500;
        } else {
          if (programme === "cumule") coutRegieLead = 5000;
          else if (programme === "174") coutRegieLead = 2000;
          else coutRegieLead = 3000;
        }
      }
    } else {
      // Lead
      if (programme === "cumule") coutRegieLead = 2500; // 1250 + 1250
      else if (programme === "174") coutRegieLead = 2500;
      else coutRegieLead = 2500; // MPR
    }
    
    // Coût précarité (1800 si bleu, 1440 si jaune)
    let coutPrecarite = 0;
    if (precarite === "BLEU") coutPrecarite = 1800;
    else if (precarite === "JAUNE") coutPrecarite = 1440;
    
    // Marge = revenus - marchandise - 4200 - cout_regie_lead - 80 - cout_precarite
    const marge = revenus - montantMarchandise - 4200 - coutRegieLead - 80 - coutPrecarite;
    return marge;
  };

  // P2-8: Save attribution
  const handleSaveAttribution = async (username) => {
    setSavingAttribution(true);
    try {
      await axios.put(`${API}/dossiers/${id}`, { assigned_to: username || "non_attribue" });
      setAssignedTo(username);
      toast.success(username ? `Dossier attribué à ${username}` : "Attribution retirée");
      fetchDossier();
    } catch (error) {
      toast.error("Erreur lors de l'attribution");
    } finally {
      setSavingAttribution(false);
    }
  };

  const handleExport = async (format = 'excel') => {
    try {
      toast.info(`Export ${format.toUpperCase()} en cours...`);
      
      const url_path = format === 'pdf' ? `${API}/export/dossier/${id}` : `${API}/dossiers/${id}/export?format=${format}`;
      const response = await axios.get(url_path, { responseType: 'blob' });
      
      const extension = format === 'excel' ? 'xlsx' : 'pdf';
      const mimeType = format === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        : 'application/pdf';
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dossier_${dossier?.client_name?.replace(/\s/g, '_')}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error(`Erreur lors de l'export ${format.toUpperCase()}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6 md:p-12 animate-pulse">
        <div className="h-8 bg-muted rounded w-1/3 mb-8"></div>
        <div className="h-40 bg-muted rounded-sm mb-6"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-16 bg-muted rounded-sm"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!dossier) return null;

  const statusConfig = STATUS_CONFIG[dossier.status] || STATUS_CONFIG.incomplete;
  const StatusIcon = statusConfig.icon;
  const validatedCount = dossier.documents.filter(d => d.status === 'validated').length;
  const pendingCount = dossier.documents.filter(d => d.status === 'pending').length;
  const rejectedCount = dossier.documents.filter(d => d.status === 'rejected').length;
  
  const nonSavSteps = dossier.steps?.filter(s => !s.key?.toLowerCase().includes("sav")) || [];
  const stepsCompleted = nonSavSteps.filter(s => s.status === 'done').length;
  const totalSteps = nonSavSteps.length;
  
  const isComplete = isLastStepDone();

  // Payment breakdown for MPR
  const paymentBreakdown = {
    cee: dossier.steps?.find(s => s.key === 'prime_cee')?.amount || 0,
    apa: dossier.steps?.find(s => s.key === 'prime_apa')?.amount || 0,
    mpr: dossier.steps?.find(s => s.key === 'prime_mpr')?.amount || 0
  };
  const paymentBreakdownRef = paymentBreakdown;

  // Payment for 174 (from "paye" or "paye_174" step)
  const payment174 = dossier.steps?.find(s => s.key === 'paye' || s.key === 'paye_174')?.amount || 0;
  
  // CA à venir for MPR = Total des aides
  const caAVenir = dossier.total_aides || 0;
  
  // Marge display
  const isApres2026 = periodeMpr === "apres_2026";
  const dynamicMarge = (() => {
    if (is174 && !isCumule) return null;
    if (isApres2026) return calculateDynamicMarge();
    // Avant 31/12/25 MPR: marge based on PAC+SSC
    if (!is174) {
      const devisStep = dossier.steps?.find(s => s.key === "devis_signe");
      if (devisStep?.status === "done") {
        const pacAmount = dossier.montant_pac || 0;
        const sscAmount = dossier.montant_ssc || 0;
        const pacSsc = pacAmount + sscAmount;
        const precarite = (dossier.precarite || "").toUpperCase();
        const baseMarge = precarite === "JAUNE" ? 3400 : 6800;
        const refPacSsc = 6580;
        if (pacSsc > 0) return baseMarge - (pacSsc - refPacSsc);
        return null; // No PAC/SSC entered yet
      }
    }
    return null;
  })();
  const displayMarge = (() => {
    // Try dynamic calculation first
    const computed = computeMargeFromDossier(dossier);
    if (computed !== null) return computed;
    return isComplete ? (dossier.marge_realisee || dossier.marge_prevue || 0) : (dossier.marge_prevue || 0);
  })();

  return (
    <div className="p-6 md:p-12 animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(`/dossiers?tab=${isCumule ? 'cumule' : is174 ? '174' : 'mpr'}`)}
          className="mb-4 -ml-2 text-muted-foreground hover:text-foreground rounded-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux dossiers
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-4xl font-extrabold tracking-tight">
                {dossier.client_name}
              </h1>
              <span className={cn(
                "doc-status",
                statusConfig.bg,
                statusConfig.color,
                statusConfig.border
              )}>
                <StatusIcon className="w-3 h-3" />
                {statusConfig.label}
              </span>
              {is174OrCumule && (
                <span className={cn("inline-flex items-center px-2 py-1 text-xs font-bold rounded-sm", isCumule ? "bg-violet-500/10 text-violet-700 border border-violet-500/30" : "bg-muted text-[hsl(145,100%,39%)] border border-border")}>
                  {isCumule ? "Cumulé" : "174"}
                </span>
              )}
            </div>
            <p className="text-muted-foreground">
              {PROFILE_LABELS[dossier.profile]} • {isCumule ? "Opération Cumulée (174 + MPR)" : dossier.programme || "Programme non défini"} • Créé le {formatDate(dossier.created_at)}
            </p>
            {is174OrCumule && dossier.nom_residence && (
              <p className="text-sm text-[hsl(145,100%,45%)] mt-1">
                {dossier.nom_residence} - {dossier.nombre_logements || 1} logement{(dossier.nombre_logements || 1) > 1 ? "s" : ""}
              </p>
            )}
            {/* P2-8: Attribution */}
            <div className="flex items-center gap-2 mt-2">
              <UserCheck className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Attribué à :</span>
              {canEditAttribution ? (
                <Select
                  value={assignedTo || "non_attribue"}
                  onValueChange={(v) => handleSaveAttribution(v === "non_attribue" ? "" : v)}
                  disabled={savingAttribution}
                >
                  <SelectTrigger className="w-[180px] h-8 rounded-sm text-sm" data-testid="attribution-select">
                    <SelectValue placeholder="Non attribué" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non_attribue">Non attribué</SelectItem>
                    {usersList.filter(u => u.active !== false).map(u => (
                      <SelectItem key={u.username} value={u.username}>{u.username}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-sm font-medium" data-testid="attribution-display">
                  {assignedTo || "Non attribué"}
                </span>
              )}
            </div>
            {/* Programme modifier — Admin/Renova only */}
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-muted-foreground">Programme :</span>
              <Select value={dossier.programme || "MPR"} onValueChange={async (val) => {
                try {
                  await axios.put(`${API}/dossiers/${id}`, { programme: val });
                  fetchDossier();
                  toast.success("Programme mis à jour");
                } catch (err) { toast.error("Erreur"); }
              }}>
                <SelectTrigger className="h-7 w-auto min-w-[160px] text-xs rounded-full border" data-testid="programme-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MPR">Programme MPR</SelectItem>
                  <SelectItem value="174">Programme 174</SelectItem>
                  <SelectItem value="cumule">Op. Cumulée (174 + MPR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Statut et ETA */}
            <div className="flex items-center gap-4 mt-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Statut :</span>
                <Select value={dossier.statut_dossier || "nouveau"} onValueChange={async (val) => {
                  try {
                    await axios.put(`${API}/dossiers/${id}`, { statut_dossier: val });
                    fetchDossier();
                    toast.success("Statut mis à jour");
                  } catch (err) { toast.error("Erreur"); }
                }}>
                  <SelectTrigger className="h-7 w-auto min-w-[140px] text-xs rounded-full border" data-testid="statut-dossier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nouveau">Nouveau</SelectItem>
                    <SelectItem value="rdv_admin">RDV Admin</SelectItem>
                    <SelectItem value="accepte">Accepté</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="travaux_en_cours">Travaux en cours</SelectItem>
                    <SelectItem value="termine">Terminé</SelectItem>
                    <SelectItem value="annule">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">ETA :</span>
                <span className="text-sm font-medium" data-testid="eta-display">{Math.round(dossier.steps_progress || 0)}%</span>
              </div>
            </div>
            {/* Status Stepper - Sequential flow */}
            {(() => {
              const STATUT_FLOW = [
                { key: "nouveau", label: "Nouveau", color: "bg-slate-400" },
                { key: "rdv_admin", label: "RDV Admin", color: "bg-amber-500" },
                { key: "accepte", label: "Accepté", color: "bg-blue-500" },
                { key: "en_cours", label: "En cours", color: "bg-indigo-500" },
                { key: "travaux_en_cours", label: "Travaux", color: "bg-orange-500" },
                { key: "termine", label: "Terminé", color: "bg-emerald-500" },
              ];
              const currentStatut = dossier.statut_dossier || "nouveau";
              const currentIdx = STATUT_FLOW.findIndex(s => s.key === currentStatut);
              const isAnnule = currentStatut === "annule";
              return (
                <div className="flex items-center gap-1 mt-3 mb-1" data-testid="status-stepper">
                  {STATUT_FLOW.map((step, idx) => {
                    const isCompleted = !isAnnule && idx <= currentIdx;
                    const isCurrent = !isAnnule && idx === currentIdx;
                    return (
                      <div key={step.key} className="flex items-center gap-1 flex-1">
                        <div className={cn(
                          "h-1.5 rounded-full flex-1 transition-all",
                          isCompleted ? step.color : "bg-muted"
                        )} title={step.label} />
                        {isCurrent && (
                          <span className="text-[10px] font-semibold text-muted-foreground whitespace-nowrap">{step.label}</span>
                        )}
                      </div>
                    );
                  })}
                  {isAnnule && (
                    <span className="text-[10px] font-semibold text-red-500 ml-2">Annulé</span>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setEditMode(true)}
              className="rounded-sm"
              data-testid="edit-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Modifier
            </Button>
            {!isRestricted && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className={cn("rounded-sm shadow-none", is174OrCumule && (isCumule ? "bg-violet-600 hover:bg-violet-700" : "bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)]"))}
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exporter
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  onClick={() => handleExport('excel')}
                  data-testid="export-excel-detail-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                  Export Excel
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleExport('pdf')}
                  data-testid="export-pdf-detail-btn"
                >
                  <FileText className="w-4 h-4 mr-2 text-red-600" />
                  Export PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Progress Overview - 4 boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {/* Box 1: Étapes (pour tous) */}
        <div className={cn(
          "border rounded-sm p-6",
          is174 ? "border-border bg-card" : "border-border bg-card"
        )}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <ListChecks className={cn("w-5 h-5", is174 ? "text-[hsl(145,100%,45%)]" : "text-primary")} />
              Étapes
            </h2>
            <span className="text-2xl font-bold tabular-nums">
              {stepsCompleted}/{totalSteps}
            </span>
          </div>
          <Progress 
            value={(stepsCompleted / totalSteps) * 100} 
            className={cn("h-3 mb-4", is174OrCumule && "[&>div]:bg-muted0")} 
          />
          <p className="text-sm text-muted-foreground">
            {stepsCompleted === totalSteps ? "Terminées" : `${totalSteps - stepsCompleted} restante${totalSteps - stepsCompleted > 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Box 2: Devis/Encaissé (174/Cumulé) ou Documents (MPR) */}
        {is174OrCumule ? (() => {
          const devisStep = dossier.steps?.find(s => s.key === 'devis_signe');
          const isDevisValidated = devisStep?.status === 'done' || isSCIMulti;
          const devisStepAmount = devisStep?.amount || 0;
          const devisValue = isSCIMulti ? (dossier.total_aides || dossier.quote_amount || 0) : isCumule ? (dossier.total_aides || devisStepAmount || 0) : (devisStepAmount || 0);
          return (
          <div className="border border-border rounded-sm bg-card p-6" data-testid="box-devis-encaisse">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Euro className="w-5 h-5 text-[hsl(145,100%,45%)]" />
                {isLastStepDone() ? "Encaissé" : "Devis"}
              </h2>
              {!isDevisValidated ? (
                <span className="text-2xl font-bold tabular-nums text-muted-foreground">—</span>
              ) : editingDevis ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={devisAmount}
                    onChange={(e) => setDevisAmount(parseFloat(e.target.value) || 0)}
                    className="w-28 h-8 text-right"
                  />
                  <Button size="sm" onClick={handleSaveDevis} className="h-8 bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)]">
                    <Save className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingDevis(false)} className="h-8">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold tabular-nums text-[hsl(145,100%,39%)]">
                    {formatCurrency(devisValue)}
                  </span>
                  {!isRestricted && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => {
                      setDevisAmount(devisValue);
                      setEditingDevis(true);
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <Pencil className="w-3 h-3 text-[hsl(145,100%,45%)]" />
                  </Button>
                  )}
                </div>
              )}
            </div>
            {isDevisValidated && isCumule ? (
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Devis 174</span>
                  <span className="font-medium tabular-nums">{formatCurrency(dossier.montant_devis_174 || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Devis MPR</span>
                  <span className="font-medium tabular-nums">{formatCurrency((dossier.total_aides || 0) - (dossier.montant_devis_174 || 0))}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-border">
                  <span className="text-muted-foreground font-semibold">Total</span>
                  <span className="font-semibold tabular-nums text-[hsl(145,100%,39%)]">{formatCurrency(dossier.total_aides || 0)}</span>
                </div>
              </div>
            ) : !isDevisValidated ? (
              <p className="text-xs text-muted-foreground">Validez l'étape "Devis signé" pour afficher le montant</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {dossier.nombre_logements || 1} logement{(dossier.nombre_logements || 1) > 1 ? "s" : ""}
              </p>
            )}
          </div>
          );
        })() : (
          <div className="border border-border rounded-sm bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Documents
              </h2>
              <span className="text-2xl font-bold tabular-nums" data-testid="progress-value">
                {dossier.progress}%
              </span>
            </div>
            <Progress value={dossier.progress} className="h-3 mb-4" />
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                <span><span className="font-medium">{validatedCount}</span> ok</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                <span><span className="font-medium">{pendingCount}</span> att.</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                <span><span className="font-medium">{rejectedCount}</span> rej.</span>
              </div>
            </div>
          </div>
        )}

        {/* Box 3: Encaissé */}
        {(() => {
          const devisStepEnc = dossier.steps?.find(s => s.key === 'devis_signe');
          const isDevisValidatedEnc = devisStepEnc?.status === 'done';
          const totalEnc = isCumule ? (payment174 + (dossier.total_payments || 0)) : is174 ? payment174 : dossier.total_payments || 0;
          return (
        <div className={cn(
          "border rounded-sm p-6",
          !isDevisValidatedEnc ? "border-border bg-card" : "border-emerald-500/30 dark:border-emerald-700 bg-emerald-500/10 dark:bg-emerald-900/40"
        )}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <Euro className={cn("w-5 h-5", isDevisValidatedEnc ? "text-emerald-600" : "text-muted-foreground")} />
              Encaissé
            </h2>
            {!isDevisValidatedEnc ? (
              <span className="text-2xl font-bold tabular-nums text-muted-foreground">—</span>
            ) : (
              <span className="text-2xl font-bold tabular-nums text-emerald-600" data-testid="total-payments">
                {formatCurrency(totalEnc)}
              </span>
            )}
          </div>
          {!isDevisValidatedEnc ? (
            <p className="text-xs text-muted-foreground">Validez l'étape "Devis signé" pour commencer le suivi</p>
          ) : isCumule ? (
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground font-semibold">174</span>
                <span className="font-medium tabular-nums">{formatCurrency(payment174)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">CEE</span>
                <span className="font-medium tabular-nums">{formatCurrency(paymentBreakdown.cee)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">APA</span>
                <span className="font-medium tabular-nums">{formatCurrency(paymentBreakdown.apa)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">MPR</span>
                <span className="font-medium tabular-nums">{formatCurrency(paymentBreakdown.mpr)}</span>
              </div>
            </div>
          ) : !is174 ? (
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">CEE</span>
                <span className="font-medium tabular-nums">{formatCurrency(paymentBreakdown.cee)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">APA</span>
                <span className="font-medium tabular-nums">{formatCurrency(paymentBreakdown.apa)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">MPR</span>
                <span className="font-medium tabular-nums">{formatCurrency(paymentBreakdown.mpr)}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Montant final payé
            </p>
          )}
        </div>
          );
        })()}

        {/* Box 4: Marge à venir / réalisée - Hidden for restricted */}
        {!isRestricted && (() => {
          const devisStepMarge = dossier.steps?.find(s => s.key === 'devis_signe');
          const isDevisValidatedMarge = devisStepMarge?.status === 'done' || isSCIMulti;
          const hasMarge = isDevisValidatedMarge && (displayMarge !== 0 || dossier.marge_prevue);
          return (
        <div className={cn(
          "border rounded-sm p-6",
          !hasMarge
            ? "border-border bg-card"
            : dynamicMarge !== null
              ? (dynamicMarge >= 0 ? "border-emerald-500/30 dark:border-emerald-700 bg-emerald-500/10 dark:bg-emerald-900/40" : "border-red-500/30 dark:border-red-700 bg-red-500/10 dark:bg-red-900/40")
              : isComplete 
                ? "border-emerald-500/30 dark:border-emerald-700 bg-emerald-500/10 dark:bg-emerald-900/40" 
                : "border-amber-500/30 dark:border-amber-700 bg-amber-500/10 dark:bg-amber-900/40"
        )}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <TrendingUp className={cn("w-5 h-5", !hasMarge ? "text-muted-foreground" : dynamicMarge !== null ? (dynamicMarge >= 0 ? "text-emerald-600" : "text-red-600") : isComplete ? "text-emerald-600" : "text-amber-600")} />
              {!hasMarge ? "Marge" : dynamicMarge !== null ? "Marge dynamique" : isComplete ? "Marge réalisée" : "Marge à venir"}
            </h2>
            {!hasMarge ? (
              <span className="text-2xl font-bold tabular-nums text-muted-foreground">—</span>
            ) : dynamicMarge !== null ? (
              <span className={cn(
                "text-2xl font-bold tabular-nums",
                dynamicMarge >= 0 ? "text-emerald-600" : "text-red-600"
              )} data-testid="dynamic-marge-value">
                {formatCurrency(dynamicMarge)}
              </span>
            ) : editingMarge ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={margeAmount}
                  onChange={(e) => setMargeAmount(parseFloat(e.target.value) || 0)}
                  className="w-28 h-8 text-right"
                />
                <Button size="sm" onClick={handleSaveMarge} className="h-8">
                  <Save className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingMarge(false)} className="h-8">
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-2xl font-bold tabular-nums",
                  isComplete ? "text-emerald-600" : "text-amber-600"
                )}>
                  {formatCurrency(displayMarge)}
                </span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => {
                    setMargeAmount(displayMarge);
                    setEditingMarge(true);
                  }}
                  className="h-8 w-8 p-0"
                >
                  <Pencil className={cn("w-3 h-3", isComplete ? "text-emerald-600" : "text-amber-600")} />
                </Button>
              </div>
            )}
          </div>
          {isCumule ? (
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Marge 174</span>
                <span className="font-medium tabular-nums">{formatCurrency(dossier.marge_174 || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Marge MPR</span>
                <span className="font-medium tabular-nums">{formatCurrency(dossier.marge_mpr || 0)}</span>
              </div>
              <div className="flex justify-between pt-1 border-t border-border">
                <span className="text-muted-foreground font-semibold">Total</span>
                <span className={cn("font-semibold tabular-nums", isComplete ? "text-emerald-600" : "text-amber-600")}>{formatCurrency(displayMarge)}</span>
              </div>
            </div>
          ) : dynamicMarge !== null ? (
            <p className="text-sm text-muted-foreground">
              Calculé en temps réel (après 2026)
            </p>
          ) : !hasMarge ? (
            <p className="text-xs text-muted-foreground">Validez l'étape "Devis signé" pour afficher la marge</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {isComplete ? "Dossier terminé" : `Basé sur ${is174OrCumule ? "le devis" : "les aides calculées"}`}
            </p>
          )}
        </div>
          );
        })()}
      </div>

      {/* Info financières section */}

      {/* Aides à venir */}
      {(isCumule || !is174) && caAVenir > 0 && (
        <div className="border border-primary/20 rounded-sm bg-primary/5 p-6 mb-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Aides à venir
            </h2>
            <span className="text-2xl font-bold tabular-nums text-primary">
              {formatCurrency(caAVenir)}
            </span>
          </div>
          {isCumule ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 text-sm">
              <div>
                <span className="text-muted-foreground">MPR</span>
                <p className="font-semibold">{formatCurrency(dossier.mpr_calcule || 0)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">CEE MPR</span>
                <p className="font-semibold">{formatCurrency(dossier.cee_calcule || 0)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">CEE 174</span>
                <p className="font-semibold">{formatCurrency(dossier.montant_devis_174 || 0)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total CEE</span>
                <p className="font-semibold">{formatCurrency((dossier.cee_calcule || 0) + (dossier.montant_devis_174 || 0))}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total aides</span>
                <p className="font-semibold text-primary">{formatCurrency(caAVenir)}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
              <div>
                <span className="text-muted-foreground">MPR calculé</span>
                <p className="font-semibold">{formatCurrency(dossier.mpr_calcule || 0)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">CEE calculé</span>
                <p className="font-semibold">{formatCurrency(dossier.cee_calcule || 0)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total</span>
                <p className="font-semibold text-primary">{formatCurrency(caAVenir)}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CA 174 seul — toujours égal à la box Devis/Encaissé */}
      {is174 && !isCumule && (dossier.montant_devis_174 || 0) > 0 && (
        <div className="border border-primary/20 rounded-sm bg-primary/5 p-6 mb-6" data-testid="box-ca-174">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              {isLastStepDone() ? "CA Encaissé 174" : "CA 174"}
            </h2>
            <span className="text-2xl font-bold tabular-nums text-primary">
              {formatCurrency(dossier.montant_devis_174 || 0)}
            </span>
          </div>
        </div>
      )}

      {/* P2-7: Contrôles financiers MPR (après 2026) - Admin only, MPR only */}
      {!is174 && !isCumule && !isRestricted && (
        <div className="border border-border rounded-sm bg-card p-6 mb-6" data-testid="financial-controls-section">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <ToggleLeft className="w-5 h-5 text-primary" />
              Contrôles financiers
            </h2>
            <div className="flex items-center gap-3">
              <span className={cn("text-sm font-medium", !isApres2026 ? "text-primary" : "text-muted-foreground")}>Avant 31/12/25</span>
              <Switch
                checked={isApres2026}
                onCheckedChange={(checked) => {
                  const val = checked ? "apres_2026" : "avant_2026";
                  setPeriodeMpr(val);
                  handleSaveFinancial("periode_mpr", val);
                }}
                data-testid="switch-periode-mpr"
              />
              <span className={cn("text-sm font-medium", isApres2026 ? "text-primary" : "text-muted-foreground")}>Après 31/12/25</span>
            </div>
          </div>

          {isApres2026 && (
            <div className="space-y-5">
              {/* Montant du devis & Montant marchandise */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground mb-1.5 block">Montant du devis</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={montantDevisMpr || ""}
                      onChange={(e) => setMontantDevisMpr(parseFloat(e.target.value) || 0)}
                      onBlur={() => handleSaveFinancial("montant_devis_mpr", montantDevisMpr)}
                      className="rounded-sm"
                      placeholder="0"
                      data-testid="input-montant-devis-mpr"
                    />
                    <span className="text-sm text-muted-foreground">€</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-1.5 block">Montant de la marchandise</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={montantMarchandise || ""}
                      onChange={(e) => setMontantMarchandise(parseFloat(e.target.value) || 0)}
                      onBlur={() => handleSaveFinancial("montant_marchandise", montantMarchandise)}
                      className="rounded-sm"
                      placeholder="0"
                      data-testid="input-montant-marchandise"
                    />
                    <span className="text-sm text-muted-foreground">€</span>
                  </div>
                </div>
              </div>

              {/* Switch Régie / Lead */}
              <div className="flex items-center gap-3">
                <Label className="text-sm text-muted-foreground">Type de coût :</Label>
                <span className={cn("text-sm font-medium", coutType === "regie" ? "text-primary" : "text-muted-foreground")}>Régie</span>
                <Switch
                  checked={coutType === "lead"}
                  onCheckedChange={(checked) => {
                    const val = checked ? "lead" : "regie";
                    setCoutType(val);
                    handleSaveFinancial("cout_type", val);
                  }}
                  data-testid="switch-cout-type"
                />
                <span className={cn("text-sm font-medium", coutType === "lead" ? "text-primary" : "text-muted-foreground")}>Lead</span>
              </div>

              {/* Détail du calcul de marge */}
              <div className="bg-muted/50 rounded-sm p-4 border border-border">
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  Calcul de marge dynamique
                </h3>
                {(() => {
                  const encaisseCee = paymentBreakdown.cee;
                  const encaisseApa = paymentBreakdown.apa;
                  const encaisseMpr = paymentBreakdown.mpr;
                  const totalEncaisse = encaisseCee + encaisseApa + encaisseMpr;
                  const revenus = totalEncaisse > 0 ? totalEncaisse : montantDevisMpr;

                  let coutRL = 0;
                  const prog = dossier.programme || "MPR";
                  const surf = dossier.metrage || 0;
                  const prec = dossier.precarite || "";
                  if (coutType === "regie") {
                    if (prec === "BLEU") {
                      if (surf < 70) { coutRL = prog === "cumule" ? 3000 : prog === "174" ? 1000 : 2000; }
                      else if (surf <= 130) { coutRL = prog === "cumule" ? 4000 : prog === "174" ? 1500 : 2500; }
                      else { coutRL = prog === "cumule" ? 5000 : prog === "174" ? 2000 : 3000; }
                    }
                  } else {
                    coutRL = prog === "cumule" ? 2500 : prog === "174" ? 2500 : 2500;
                  }

                  let coutPrec = 0;
                  if (prec === "BLEU") coutPrec = 1800;
                  else if (prec === "JAUNE") coutPrec = 1440;

                  const marge = revenus - montantMarchandise - 4200 - coutRL - 80 - coutPrec;

                  return (
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          {totalEncaisse > 0 ? "Somme encaissés (CEE+APA+MPR)" : "Montant devis (encaissés = 0)"}
                        </span>
                        <span className="font-medium text-emerald-600 tabular-nums">+ {formatCurrency(revenus)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Montant marchandise</span>
                        <span className="font-medium text-red-500 tabular-nums">- {formatCurrency(montantMarchandise)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Frais fixes</span>
                        <span className="font-medium text-red-500 tabular-nums">- 4 200 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Coût {coutType === "regie" ? "régie" : "lead"} {coutType === "regie" && prec !== "BLEU" ? "(non applicable)" : ""}</span>
                        <span className="font-medium text-red-500 tabular-nums">- {formatCurrency(coutRL)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Frais divers</span>
                        <span className="font-medium text-red-500 tabular-nums">- 80 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Coût précarité ({prec || "N/A"})</span>
                        <span className="font-medium text-red-500 tabular-nums">- {formatCurrency(coutPrec)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-border">
                        <span className="font-semibold">Marge calculée</span>
                        <span className={cn("text-lg font-bold tabular-nums", marge >= 0 ? "text-emerald-600" : "text-red-600")}>
                          {formatCurrency(marge)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs for Steps and Info */}
      <Tabs defaultValue="steps" className="space-y-6">
        <TabsList className={cn("rounded-sm p-1", is174 ? "bg-muted" : "bg-muted")}>
          <TabsTrigger value="steps" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-steps">
            <ListChecks className="w-4 h-4 mr-2" />
            Étapes ({totalSteps})
          </TabsTrigger>
          {(!is174 || isCumule) && (
            <TabsTrigger value="documents" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-documents">
              <FileText className="w-4 h-4 mr-2" />
              Documents ({dossier.documents.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="files" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-files">
            <File className="w-4 h-4 mr-2" />
            Fichiers ({(dossier.stored_files?.length || 0) + (dossier.files?.length || 0)})
          </TabsTrigger>
          <TabsTrigger value="info" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-info">
            <User className="w-4 h-4 mr-2" />
            Informations
          </TabsTrigger>
          {!isRestricted && (
          <TabsTrigger value="travaux" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-travaux">
              <Hammer className="w-4 h-4 mr-2" />
              Reco. travaux
            </TabsTrigger>
          )}
          {dossier.simulation_data && !isRestricted && (
            <TabsTrigger value="simulation" className="rounded-sm data-[state=active]:bg-card" data-testid="tab-simulation">
              <Calculator className="w-4 h-4 mr-2" />
              Résultat Simulation
            </TabsTrigger>
          )}
        </TabsList>

        {/* Steps Tab */}
        <TabsContent value="steps">
          {isSCIMulti ? (
            <SCIEtapesTab dossier={dossier} dossierId={id} fetchDossier={fetchDossier} />
          ) : is174OrCumule ? (
            <Etapes174 
              dossier={dossier}
              savingStep={savingStep}
              onStepStatusChange={(stepKey, status) => handleStepStatusChange(stepKey, status, null, null, null)}
              onStepDateChange={handleStepDateChange}
              onStepAmountChange={handleStepAmountChange}
              isCumule={isCumule}
              onDevisAmountChange={(stepKey, amount, programme) => {
                fetchDossier();
              }}
              API={API}
              dossierId={id}
              fetchDossier={fetchDossier}
              isRestricted={isRestricted}
            />
          ) : (
            <EtapesPacSsc
              dossier={dossier}
              savingStep={savingStep}
              amountInputs={amountInputs}
              completionDate={completionDate}
              onStepStatusChange={handleStepStatusChange}
              onAmountChange={handleAmountChange}
              onAmountSave={handleAmountSave}
              onCompletionDateSave={handleCompletionDateSave}
              onSousTraitantChange={handleSousTraitantChange}
              API={API}
              id={id}
              fetchDossier={fetchDossier}
              isRestricted={isRestricted}
            />
          )}
        </TabsContent>

        {/* Documents Tab - For MPR and Cumulé */}
        {(!is174 || isCumule) && (
          <TabsContent value="documents">
            <div className="border border-border rounded-sm bg-card">
              {/* Header avec bouton Valider tout */}
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documents ({dossier.documents.filter(d => d.status === 'validated').length}/{dossier.documents.length} valides)
                  {dossier.documents.filter(d => d.status === 'awaiting_validation').length > 0 && (
                    <span className="ml-2 text-xs text-blue-600 font-normal">
                      ({dossier.documents.filter(d => d.status === 'awaiting_validation').length} en attente de validation)
                    </span>
                  )}
                </h3>
                <Button
                  onClick={handleValidateAllDocuments}
                  disabled={validatingAllDocs || dossier.documents.every(d => d.status === 'validated')}
                  variant="outline"
                  size="sm"
                  className="rounded-sm bg-emerald-500/10 border-emerald-500/30 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
                  data-testid="validate-all-docs-btn"
                >
                  {validatingAllDocs ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCheck className="w-4 h-4 mr-2" />
                  )}
                  Valider tous les documents
                </Button>
              </div>
              <div className="divide-y divide-border">
                {dossier.documents.map((doc) => {
                  const docStatusConfig = DOC_STATUS_CONFIG[doc.status] || DOC_STATUS_CONFIG.pending;
                  const DocStatusIcon = docStatusConfig.icon;
                  const isSaving = savingDoc === doc.key;
                  
                  return (
                    <div 
                      key={doc.key}
                      className="p-4 flex items-center justify-between"
                      data-testid={`doc-${doc.key}`}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className={cn(
                          "w-10 h-10 rounded-sm flex items-center justify-center shrink-0",
                          docStatusConfig.bg
                        )}>
                          <DocStatusIcon className={cn("w-5 h-5", docStatusConfig.color)} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-sm text-muted-foreground truncate">{doc.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 ml-4">
                        <Select
                          value={doc.status}
                          onValueChange={(value) => handleDocumentStatusChange(doc.key, value)}
                          disabled={isSaving}
                        >
                          <SelectTrigger 
                            className={cn(
                              "w-[160px] rounded-sm",
                              docStatusConfig.bg,
                              docStatusConfig.border,
                              "border"
                            )}
                            data-testid={`doc-status-select-${doc.key}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="validated">
                              <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                Valide
                              </div>
                            </SelectItem>
                            <SelectItem value="awaiting_validation">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-blue-600" />
                                En attente de validation
                              </div>
                            </SelectItem>
                            <SelectItem value="pending">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-amber-600" />
                                En attente
                              </div>
                            </SelectItem>
                            <SelectItem value="rejected">
                              <div className="flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-600" />
                                Rejete
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {isSaving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                      </div>
                    </div>
                  );
                })}
                
                {/* Complementary Document */}
                <div 
                  className="p-4 flex items-center justify-between bg-muted/30"
                  data-testid="doc-complementary"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className={cn(
                      "w-10 h-10 rounded-sm flex items-center justify-center shrink-0",
                      COMP_DOC_STATUS_CONFIG[compDocStatus]?.bg || "bg-muted"
                    )}>
                      {(() => {
                        const StatusIcon = COMP_DOC_STATUS_CONFIG[compDocStatus]?.icon || Circle;
                        return <StatusIcon className={cn("w-5 h-5", COMP_DOC_STATUS_CONFIG[compDocStatus]?.color)} />;
                      })()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-muted-foreground mb-1">Doc complémentaire</p>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Nom du document..."
                          value={compDocName}
                          onChange={(e) => setCompDocName(e.target.value)}
                          className="rounded-sm h-8 text-sm max-w-[250px]"
                          data-testid="comp-doc-name-input"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCompDocNameSave}
                          disabled={savingCompDoc}
                          className="rounded-sm h-8"
                          data-testid="save-comp-doc-name"
                        >
                          <Save className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4">
                    <Select
                      value={compDocStatus}
                      onValueChange={handleCompDocUpdate}
                      disabled={savingCompDoc}
                    >
                      <SelectTrigger 
                        className={cn(
                          "w-[160px] rounded-sm",
                          COMP_DOC_STATUS_CONFIG[compDocStatus]?.bg,
                          COMP_DOC_STATUS_CONFIG[compDocStatus]?.border,
                          "border"
                        )}
                        data-testid="comp-doc-status-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="non_demande">
                          <div className="flex items-center gap-2">
                            <Circle className="w-4 h-4 text-muted-foreground" />
                            Non demandé
                          </div>
                        </SelectItem>
                        <SelectItem value="en_attente">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-amber-600" />
                            En attente
                          </div>
                        </SelectItem>
                        <SelectItem value="transmis">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            Transmis
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {savingCompDoc && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Files Tab */}
        <TabsContent value="files">
          <div className={cn(
            "border rounded-sm p-6",
            is174 ? "border-border bg-card" : "border-border bg-card"
          )}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <File className={cn("w-5 h-5", is174 ? "text-[hsl(145,100%,45%)]" : "text-primary")} />
                Fichiers stockés
              </h2>
              <div className="flex items-center gap-2">
                {/* Bouton Envoyer lien de dépôt */}
                <Button
                  variant="default"
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => setShowUploadLinkModal(true)}
                  data-testid="send-upload-link-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer lien de dépôt
                </Button>
                
                {/* Bouton Importer APF */}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleApfUpload}
                    className="hidden"
                    data-testid="apf-upload-input"
                  />
                  <Button
                    variant="default"
                    className={cn(
                      "rounded-sm",
                      "bg-amber-500 hover:bg-amber-600 text-white"
                    )}
                    disabled={uploadingApf}
                    asChild
                  >
                    <span>
                      {uploadingApf ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4 mr-2" />
                      )}
                      Importer APF
                    </span>
                  </Button>
                </label>
                
                {/* Bouton Ajouter PDF */}
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <Button
                    variant="outline"
                    className={cn("rounded-sm", is174 && "border-border hover:bg-muted")}
                    disabled={uploadingFile}
                    asChild
                  >
                    <span>
                      {uploadingFile ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Ajouter un PDF
                    </span>
                  </Button>
                </label>
              </div>
            </div>

            {/* Section Références APF extraites */}
            {dossier.apf_references?.length > 0 && (
              <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Références APF ({dossier.apf_references.length})
                </h3>
                <div className="space-y-3">
                  {dossier.apf_references.map((apf, index) => (
                    <div key={index} className="bg-card p-3 rounded border border-amber-100">
                      <div className="flex flex-wrap gap-2 items-center mb-2">
                        {apf.numero_aaf && (
                          <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-mono rounded">
                            {apf.numero_aaf}
                          </span>
                        )}
                        {apf.montant_total_ttc && (
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded">
                            {formatCurrency(apf.montant_total_ttc)} TTC
                          </span>
                        )}
                        {apf.date_paiement_prevue && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            Paiement prévu: {apf.date_paiement_prevue}
                          </span>
                        )}
                      </div>
                      {apf.numeros_dossiers?.length > 0 && (
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium">N° dossiers:</span>{' '}
                          {apf.numeros_dossiers.join(', ')}
                        </div>
                      )}
                      {apf.beneficiaires?.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          <span className="font-medium">Bénéficiaires:</span>{' '}
                          {apf.beneficiaires.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dossier.stored_files?.length > 0 ? (
              <div className="space-y-3">
                {dossier.stored_files.map((file) => {
                  // Déterminer l'icône selon le type de fichier
                  const ext = file.original_name?.split('.').pop()?.toLowerCase();
                  const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
                  const isPdf = ext === 'pdf';
                  
                  // Labels pour les types de documents
                  const docTypeLabels = {
                    justificatif_domicile: "Justificatif domicile",
                    avis_imposition: "Avis d'imposition",
                    titre_propriete: "Titre de propriété",
                    piece_identite: "Pièce d'identité",
                    devis: "Devis",
                    facture: "Facture",
                    autre: "Autre"
                  };
                  
                  const docTypeLabel = file.document_type ? docTypeLabels[file.document_type] || file.document_type : null;
                  const isClientUpload = file.uploaded_by === 'client';
                  
                  return (
                    <div 
                      key={file.id}
                      className={cn(
                        "flex items-center justify-between p-4 border rounded-sm transition-colors",
                        isClientUpload 
                          ? "border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/10" 
                          : is174OrCumule ? "border-border bg-card/50 hover:bg-muted" : "border-border bg-muted/30 hover:bg-muted/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-sm flex items-center justify-center",
                          isImage ? "bg-blue-100" : isPdf ? "bg-red-100" : "bg-muted"
                        )}>
                          {isImage ? (
                            <img src="" alt="" className="hidden" />
                          ) : null}
                          <FileText className={cn(
                            "w-5 h-5",
                            isImage ? "text-blue-600" : isPdf ? "text-red-600" : "text-muted-foreground"
                          )} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{file.original_name}</p>
                          <div className="flex items-center gap-2 flex-wrap mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {new Date(file.uploaded_at).toLocaleDateString('fr-FR')}
                            </span>
                            {isClientUpload && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded">
                                <User className="w-3 h-3" />
                                Client{file.client_name ? ` (${file.client_name})` : ''}
                              </span>
                            )}
                            {docTypeLabel && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded">
                                {docTypeLabel}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFileDownload(file.id, file.original_name)}
                          className="rounded-sm"
                          data-testid={`download-file-${file.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFileDelete(file.id)}
                          className="rounded-sm text-destructive hover:text-destructive"
                          data-testid={`delete-file-${file.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <File className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Aucun fichier stocké</p>
                <p className="text-sm mt-1">Ajoutez des fichiers PDF pour les conserver avec ce dossier</p>
              </div>
            )}

            {/* New files (from /dossiers/:id/files endpoint) */}
            {dossier.files?.length > 0 && (
              <div className="mt-4 border border-border rounded-sm p-4">
                <h3 className="font-semibold text-sm mb-3">Documents déposés</h3>
                <div className="space-y-2">
                  {dossier.files.map(f => (
                    <div key={f.id} className="flex items-center justify-between p-3 border border-border rounded-sm bg-muted/20">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-red-500" />
                        <div>
                          <p className="text-sm font-medium">{f.filename}</p>
                          <p className="text-[10px] text-muted-foreground">
                            Par {f.uploaded_by || "Inconnu"} — {f.created_at ? new Date(f.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                          </p>
                        </div>
                      </div>
                      <a href={`${API}/dossiers/${id}/files/${f.id}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded hover:bg-muted">
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Notes — shared across all users */}
          <div className={cn("border rounded-sm p-6 mt-4", "border-border bg-card")}>
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2 mb-4">
              <StickyNote className="w-5 h-5 text-amber-500" />
              Notes
            </h2>
            <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
              {(() => {
                const notesList = Array.isArray(dossier.notes_list) ? dossier.notes_list : [];
                if (notesList.length > 0) {
                  return notesList.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map((note, i) => (
                    <div key={note.id || i} className="p-3 rounded-sm border border-border bg-muted/20">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold">{note.author || "Inconnu"}</span>
                        <span className="text-[10px] text-muted-foreground">{note.created_at ? new Date(note.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                      </div>
                      <p className="text-sm">{note.content}</p>
                    </div>
                  ));
                }
                // Fallback: parse string notes
                const notesStr = typeof dossier.notes === "string" ? dossier.notes : "";
                if (!notesStr.trim()) return <p className="text-sm text-muted-foreground italic">Aucune note</p>;
                return notesStr.split("\n\n").filter(n => n.trim()).map((block, i) => {
                  const match = block.match(/^\[(.+?) — (.+?)\]\n([\s\S]*)$/);
                  if (match) return (
                    <div key={i} className="p-3 rounded-sm border border-border bg-muted/20">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold">{match[2]}</span>
                        <span className="text-[10px] text-muted-foreground">{match[1]}</span>
                      </div>
                      <p className="text-sm">{match[3]}</p>
                    </div>
                  );
                  return <div key={i} className="p-3 rounded-sm border border-border bg-muted/20 text-sm">{block}</div>;
                });
              })()}
            </div>
            <div className="flex gap-2">
              <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Ajouter une note..." className="rounded-sm flex-1" data-testid="note-input-fichiers" />
              <Button disabled={!newNote.trim() || savingNote} onClick={handleAddNote} className="rounded-sm">
                {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
              </Button>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="info">
          {isSCIMulti ? (
            <SCIInfoTab dossier={dossier} dossierId={id} fetchDossier={fetchDossier} />
          ) : (
          <div className="space-y-6">
            {/* Informations client - EDITABLE */}
            <div className="border rounded-sm p-6 border-border bg-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                  <User className={cn("w-5 h-5", is174 ? "text-[hsl(145,100%,45%)]" : "text-primary")} />
                  Informations client
                </h2>
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => {
                  setEditData({
                    client_name: dossier.client_name || "",
                    client_email: dossier.client_email || "",
                    client_phone: dossier.client_phone || "",
                    zone: dossier.zone || "",
                    metrage: dossier.metrage || "",
                    precarite: dossier.precarite || "",
                    code_postal: dossier.code_postal || "",
                    adresse_logement: dossier.adresse_logement || "",
                    organisme_bailleur: dossier.organisme_bailleur || "",
                    nom_representant: dossier.nom_representant || "",
                    tel_representant: dossier.tel_representant || "",
                    fixe_representant: dossier.fixe_representant || "",
                    email_representant: dossier.email_representant || "",
                    adresse_bailleur: dossier.adresse_bailleur || "",
                    nom_residence: dossier.nom_residence || "",
                    adresse_residence: dossier.adresse_residence || "",
                    coordonnees_gardien: dossier.coordonnees_gardien || "",
                    nombre_logements: dossier.nombre_logements || 1,
                    notes: dossier.notes || "",
                  });
                  setEditMode(true);
                }} data-testid="edit-info-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />
                  Modifier
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="text-sm"><span className="text-muted-foreground mr-1">Nom :</span><span className="font-medium">{dossier.client_name || <span className="text-muted-foreground italic">Non renseigné</span>}</span></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="text-sm"><span className="text-muted-foreground mr-1">Email :</span><span className="font-medium">{dossier.client_email || dossier.email_representant || <span className="text-muted-foreground italic">Non renseigné</span>}</span></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="text-sm"><span className="text-muted-foreground mr-1">Tél :</span><span className="font-medium">{dossier.client_phone || dossier.tel_representant || <span className="text-muted-foreground italic">Non renseigné</span>}</span></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm">Mis à jour le {formatDate(dossier.updated_at)}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="text-sm">
                      <span className="text-muted-foreground mr-1">Zone :</span>
                      {dossier.zone ? (
                        <span className={cn("inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-sm",
                          is174 ? "bg-muted text-[hsl(145,100%,39%)] border-border" : "bg-primary/10 text-primary border-primary/20"
                        )}>{ZONE_LABELS[dossier.zone] || dossier.zone}</span>
                      ) : <span className="text-muted-foreground italic">Non renseigné</span>}
                    </div>
                  </div>
                  {dossier.adresse_logement && (
                    <div className="flex items-center gap-3">
                      <Home className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="text-sm"><span className="text-muted-foreground mr-1">Adresse :</span><span className="font-medium">{dossier.adresse_logement}</span></div>
                    </div>
                  )}
                  {dossier.code_postal && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="text-sm"><span className="text-muted-foreground mr-1">Code postal :</span><span className="font-medium">{dossier.code_postal}</span></div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Ruler className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="text-sm"><span className="text-muted-foreground mr-1">Métrage :</span>{dossier.metrage ? <span className="font-medium">{dossier.metrage} m²</span> : <span className="text-muted-foreground italic">Non renseigné</span>}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="text-sm"><span className="text-muted-foreground mr-1">Précarité :</span>
                      {dossier.precarite && PRECARITE_CONFIG[dossier.precarite] ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className={cn("w-3 h-3 rounded-full", PRECARITE_CONFIG[dossier.precarite].color)}></span>
                          <span className="font-medium">{PRECARITE_CONFIG[dossier.precarite].label}</span>
                        </span>
                      ) : <span className="text-muted-foreground italic">Non renseigné</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Organisme Bailleur - Only for bailleur/sci profiles */}
            {(dossier.profile === 'bailleur' || dossier.profile === 'sci') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-border rounded-sm bg-card p-6">
                  <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-4 text-foreground">
                    <Building className="w-5 h-5" />
                    Organisme Bailleur
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-muted-foreground">Organisme :</span> {dossier.organisme_bailleur || "-"}</p>
                    <p><span className="text-muted-foreground">Représentant :</span> {dossier.nom_representant || "-"}</p>
                    <p><span className="text-muted-foreground">Tél :</span> {dossier.tel_representant || "-"} {dossier.fixe_representant && `/ ${dossier.fixe_representant}`}</p>
                    <p><span className="text-muted-foreground">Email :</span> {dossier.email_representant || "-"}</p>
                    <p><span className="text-muted-foreground">Adresse :</span> {dossier.adresse_bailleur || "-"}</p>
                  </div>
                </div>
                <div className="border border-border rounded-sm bg-card p-6">
                  <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-4 text-foreground">
                    <Home className="w-5 h-5" />
                    Résidence
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-muted-foreground">Nom :</span> {dossier.nom_residence || "-"}</p>
                    <p><span className="text-muted-foreground">Adresse :</span> {dossier.adresse_residence || "-"}</p>
                    <p><span className="text-muted-foreground">Gardien :</span> {dossier.coordonnees_gardien || "-"}</p>
                    <p><span className="text-muted-foreground">Type :</span> {dossier.type_logements === "maison" ? "Maisons" : "Appartements"}</p>
                    <p><span className="text-muted-foreground">Nombre :</span> {dossier.nombre_logements || 1}</p>
                    <p><span className="text-muted-foreground">Zone :</span> {dossier.zone ? ZONE_LABELS[dossier.zone] : "-"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* SCI Details */}
            {dossier.profile === 'sci' && dossier.sci_nom && (
              <div className="border border-border rounded-sm bg-card p-6">
                <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-4 text-foreground">
                  <Building className="w-5 h-5" />
                  Informations SCI
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <p><span className="text-muted-foreground">Nom :</span> {dossier.sci_nom || "-"}</p>
                  <p><span className="text-muted-foreground">SIRET :</span> {dossier.sci_siret || "-"}</p>
                  <p><span className="text-muted-foreground">Adresse :</span> {dossier.sci_adresse || "-"}</p>
                  <p><span className="text-muted-foreground">Gérant :</span> {dossier.sci_gerant || "-"}</p>
                </div>
              </div>
            )}
            
            {/* Notes Section */}
            <div className="border rounded-sm p-6 border-border bg-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-muted-foreground" />
                  Notes
                </h3>
              </div>
              
              {dossier.notes && (
                <div className="p-4 rounded-sm mb-4 whitespace-pre-wrap text-sm bg-muted/50">
                  {dossier.notes}
                </div>
              )}
              
              <div className="space-y-3">
                <Textarea
                  placeholder="Ajouter une note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="rounded-sm min-h-[80px]"
                  data-testid="new-note-input"
                />
                <Button
                  onClick={handleAddNote}
                  disabled={savingNote || !newNote.trim()}
                  className={cn("rounded-sm", is174 && "bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)]")}
                  data-testid="add-note-btn"
                >
                  {savingNote ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Ajouter une note
                </Button>
              </div>
            </div>
          </div>
          )}
        </TabsContent>

        {/* Travaux Tab */}
          <TabsContent value="travaux">
            {isSCIMulti ? (
              <SCITravauxTab dossier={dossier} dossierId={id} fetchDossier={fetchDossier} />
            ) : (
            <TravauxTab
              dossier={dossier}
              is174={is174}
              formatCurrency={formatCurrency}
              onSave={async (data) => {
                await axios.put(`${API}/dossiers/${id}`, data);
                fetchDossier();
              }}
            />
            )}
          </TabsContent>

        {/* Simulation Results Tab */}
        {dossier.simulation_data && (
          <TabsContent value="simulation">
            <div className="border border-border rounded-lg bg-card p-6 space-y-4">
              <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                <Calculator className="w-5 h-5 text-indigo-500" />
                Résultats de la simulation d'origine
              </h3>
              <SimulationResultsTab simulationData={dossier.simulation_data} />
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Travaux Reminder Dialog */}
      <Dialog open={showTravauxReminder} onOpenChange={setShowTravauxReminder}>
        <DialogContent className="rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Hammer className="w-5 h-5 text-amber-500" />
              Rappel : Travaux
            </DialogTitle>
            <DialogDescription>
              Le devis est signé ! N'oubliez pas de renseigner les travaux prévus dans l'onglet <strong>"Travaux"</strong> pour un calcul de marge précis.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowTravauxReminder(false)} className="rounded-xl">
              Plus tard
            </Button>
            <Button onClick={() => { setShowTravauxReminder(false); /* Switch to travaux tab - handled by UI */ }}
              className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white">
              Aller aux travaux
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editMode} onOpenChange={setEditMode}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Modifier le dossier</DialogTitle>
            <DialogDescription>
              Modifiez les informations du {isCumule ? "dossier cumulé" : is174 ? "dossier 174" : "client"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Common fields for all programmes */}
              <div className="form-group">
                <Label>Nom du client / bénéficiaire</Label>
                <Input value={editData.client_name || ""} onChange={(e) => setEditData({ ...editData, client_name: e.target.value })}
                  className="rounded-sm" data-testid="edit-name-input" />
              </div>
              <div className="form-group">
                <Label>Email</Label>
                <Input type="email" value={editData.client_email || ""} onChange={(e) => setEditData({ ...editData, client_email: e.target.value })}
                  className="rounded-sm" data-testid="edit-email-input" />
              </div>
              <div className="form-group">
                <Label>Téléphone</Label>
                <Input value={editData.client_phone || ""} onChange={(e) => setEditData({ ...editData, client_phone: e.target.value })}
                  className="rounded-sm" data-testid="edit-phone-input" />
              </div>
              <div className="form-group">
                <Label>Code postal</Label>
                <Input value={editData.code_postal || ""} onChange={(e) => setEditData({ ...editData, code_postal: e.target.value })}
                  className="rounded-sm" data-testid="edit-cp-input" />
              </div>
              <div className="form-group col-span-2">
                <Label>Adresse du logement</Label>
                <Input value={editData.adresse_logement || ""} onChange={(e) => setEditData({ ...editData, adresse_logement: e.target.value })}
                  className="rounded-sm" data-testid="edit-adresse-input" />
              </div>
              <div className="form-group">
                <Label>Métrage (m²)</Label>
                <Input type="number" value={editData.metrage || ""} onChange={(e) => setEditData({ ...editData, metrage: e.target.value })}
                  className="rounded-sm" data-testid="edit-metrage-input" />
              </div>
              <div className="form-group">
                <Label>Nombre de logements</Label>
                <Input type="number" min="1" value={editData.nombre_logements || 1} onChange={(e) => setEditData({ ...editData, nombre_logements: parseInt(e.target.value) || 1 })}
                  className="rounded-sm" />
              </div>
              <div className="form-group">
                <Label>Zone climatique</Label>
                <Select value={editData.zone || ""} onValueChange={(value) => setEditData({ ...editData, zone: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="edit-zone-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="H1">H1 - Nord et Est</SelectItem>
                    <SelectItem value="H2">H2 - Ouest et Centre</SelectItem>
                    <SelectItem value="H3">H3 - Sud et Méditerranée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Précarité</Label>
                <Select value={editData.precarite || ""} onValueChange={(value) => setEditData({ ...editData, precarite: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="edit-precarite-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bleu">Bleu - Très modeste</SelectItem>
                    <SelectItem value="jaune">Jaune - Modeste</SelectItem>
                    <SelectItem value="violet">Violet - Intermédiaire</SelectItem>
                    <SelectItem value="rose">Rose - Supérieur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Bailleur / Représentant */}
            <details className="border border-border rounded-sm p-3">
              <summary className="text-sm font-semibold cursor-pointer">Informations bailleur / représentant</summary>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="form-group">
                  <Label>Organisme bailleur</Label>
                  <Input value={editData.organisme_bailleur || ""} onChange={(e) => setEditData({ ...editData, organisme_bailleur: e.target.value })} className="rounded-sm" />
                </div>
                <div className="form-group">
                  <Label>Nom du représentant</Label>
                  <Input value={editData.nom_representant || ""} onChange={(e) => setEditData({ ...editData, nom_representant: e.target.value })} className="rounded-sm" />
                </div>
                <div className="form-group">
                  <Label>Tél. représentant</Label>
                  <Input value={editData.tel_representant || ""} onChange={(e) => setEditData({ ...editData, tel_representant: e.target.value })} className="rounded-sm" />
                </div>
                <div className="form-group">
                  <Label>Tél. fixe</Label>
                  <Input value={editData.fixe_representant || ""} onChange={(e) => setEditData({ ...editData, fixe_representant: e.target.value })} className="rounded-sm" />
                </div>
                <div className="form-group">
                  <Label>Email représentant</Label>
                  <Input type="email" value={editData.email_representant || ""} onChange={(e) => setEditData({ ...editData, email_representant: e.target.value })} className="rounded-sm" />
                </div>
                <div className="form-group">
                  <Label>Adresse bailleur</Label>
                  <Input value={editData.adresse_bailleur || ""} onChange={(e) => setEditData({ ...editData, adresse_bailleur: e.target.value })} className="rounded-sm" />
                </div>
                <div className="form-group">
                  <Label>Nom de la résidence</Label>
                  <Input value={editData.nom_residence || ""} onChange={(e) => setEditData({ ...editData, nom_residence: e.target.value })} className="rounded-sm" />
                </div>
                <div className="form-group">
                  <Label>Adresse résidence</Label>
                  <Input value={editData.adresse_residence || ""} onChange={(e) => setEditData({ ...editData, adresse_residence: e.target.value })} className="rounded-sm" />
                </div>
                <div className="form-group">
                  <Label>Coordonnées gardien</Label>
                  <Input value={editData.coordonnees_gardien || ""} onChange={(e) => setEditData({ ...editData, coordonnees_gardien: e.target.value })} className="rounded-sm" />
                </div>
              </div>
            </details>
            {/* Notes */}
            <div className="form-group">
              <Label>Notes</Label>
              <Input value={editData.notes || ""} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} className="rounded-sm" data-testid="edit-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMode(false)} className="rounded-sm" data-testid="cancel-edit-btn">
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} className={cn("rounded-sm", is174 && "bg-[hsl(145,100%,45%)] hover:bg-[hsl(145,100%,39%)]")} data-testid="save-edit-btn">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal pour envoyer un lien de dépôt client */}
      <SendUploadLinkModal
        isOpen={showUploadLinkModal}
        onClose={() => setShowUploadLinkModal(false)}
        entityType="dossier"
        entityId={id}
        clientName={dossier?.client_name}
        clientEmail={dossier?.client_email}
      />
    </div>
  );
}

